import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { isAndroid, isIOS } from 'react-device-detect';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AiOutlineUser } from 'react-icons/ai';
import { BiChevronDown, BiChevronUp, BiSolidRocket } from 'react-icons/bi';
import { SlUserFemale } from 'react-icons/sl';
import { toast } from 'react-toastify';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import PopoverWithAudio, { IPopoverWithAudioType } from '../../components/common/PopoverWithAudio';
import ProBadge from '../../components/ui/ProBadge';
import { AlertsType } from '../../constants/alerts.constant';
import usePermission from '../../hooks/usePermission';
import Utilities from '../../utils';
import AppearanceCustomCss from './AppearanceCustomCss';
import AppearanceCustomPoweredBy from './AppearanceCustomPoweredBy';
import AppearanceDisplayName from './AppearanceDisplayName';
import AppearanceDownTime from './AppearanceDownTime';
import AppearanceMessageNotificationComponent from './AppearanceMessageNotification.component';
import AppearanceMessageSound from './AppearanceMessageSound.component';
import AppearanceNotificationComponent from './AppearanceNotification.component';
import { handleResetAppearance, handleUpdateAppearanceItem, selectAppearanceState } from './appearanceSlice';
import AppearanceVoiceResponseToggle from './AppearanceVoiceResponseToggle';
import AvatarSelector from './AvatarSelector';
import ColorDropDown from './ColorDropDown';
import IconCustomizationSection from './IconCustomizationSection';
import { IAiAppearance } from './types';

export const BiLouise = () => {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M26.6836 7.74844H17.9565V3.57422L5.27734 16.2622V24.2907H13.9973V28.4631L22.4276 20.0329H26.6836V7.74844ZM9.53334 20.0329V12.0044H22.4276V20.0329H9.53334Z"
        fill="white"
      />
    </svg>
  );
};

const AppearanceSharedAi = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useAppDispatch();

  const permissions = usePermission();
  const { configs, originalData } = useAppSelector(selectAppearanceState);

  const avatarRef = useRef<HTMLInputElement | null>(null);

  const iconImageRef = useRef<HTMLInputElement | null>(null);
  const iconVideoRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
    control,
  } = useFormContext<IAiAppearance>();

  const formContext = {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors },
    control,
  };

  // ========================= Avatar ============================ //
  const [avatarsByUser, setAvatarsByUser] = useState<string | ArrayBuffer | null>();

  const handleSelectCustomAvatar = () => {
    if (avatarRef.current) {
      avatarRef.current.value = '';
      avatarRef.current.click();
    }
  };

  const handleVerifyAndSelectCustomAvatar = (e: any) => {
    if (e.target.files && e.target.files[0]) {
      const file: File = e.target.files[0];

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return false;
      }

      if (file.size / (1024 * 1024) > 5) {
        toast.error(t(AlertsType.ISSTKO008, { size: 5 }));
        return false;
      }

      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type)) {
        toast.error(t(AlertsType.ISSTKO009));
        return false;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setAvatarsByUser(reader.result);
        handleUpdateItem('logo', file);
      };
      reader.onerror = error => console.log(error);
    }
  };

  const handleRemoveClientAvatar = () => {
    setAvatarsByUser(null);
    handleUpdateItem('logo', '');
  };

  // ========================= General ============================ //
  const handleUpdateItem = (key: keyof IAiAppearance, value: any) => {
    if (key === 'icon' && value.type === 'image' && value.value && typeof value.value === 'object') {
      const file: File = value.value;

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return;
      }

      if (file.size / (1024 * 1024) > 5) {
        toast.error(t(AlertsType.ISSTKO008, { size: 5 }));
        return;
      }

      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type)) {
        toast.error(t(AlertsType.ISSTKO009));
        return;
      }
    }

    if (key === 'icon' && value.type === 'video' && value.value && typeof value.value === 'object') {
      const file: File = value.value;

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return;
      }

      if (file.size / (1024 * 1024) > 30) {
        toast.error(t(AlertsType.ISSTKO008, { size: 30 }));
        return;
      }

      if (file?.type) {
        if (!Utilities.isSupportedVideoFormatMimeType(file.type)) {
          toast.error(t(AlertsType.ISSTKO009));
          return;
        }
      } else if (file?.name) {
        if (!Utilities.isSupportedVideoFormatWithFileName(file.name)) {
          toast.error(t(AlertsType.ISSTKO009));
          return;
        }
      }
    }

    setValue(key, value);
    dispatch(handleUpdateAppearanceItem({ key, value }));
  };

  // Popover voice and texts
  const voiceCommandPopData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-14'),
  };

  const SaveConversationData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-08'),
  };

  const SaveStatsData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-09'),
  };

  useEffect(() => {
    if (
      configs?.logo &&
      configs.logo !== 'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png' &&
      configs.logo !== 'https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png' &&
      typeof configs.logo !== 'object'
    ) {
      setAvatarsByUser(configs.logo);
      handleUpdateItem('logo', configs?.logo);
    }
  }, [configs?.logo]);

  return (
    <div className="appearance-shared-ai tw-w-full">
      <React.Fragment>
        {/* Avatar */}
        <AvatarSelector
          t={t}
          permissions={permissions}
          handleUpdateItem={handleUpdateItem}
          handleRemoveClientAvatar={handleRemoveClientAvatar}
          handleSelectCustomAvatar={handleSelectCustomAvatar}
          handleVerifyAndSelectCustomAvatar={handleVerifyAndSelectCustomAvatar}
          configs={configs}
          avatarsByUser={avatarsByUser}
          avatarRef={avatarRef}
          errors={errors}
        />
        <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />
        <AppearanceDisplayName errors={errors} register={register} />

        {/* Theme */}
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t('DashboardSettingCustomizeChat/l-06')}
          </div>
          <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full">
            <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-w-full">
              <div className="tw-w-full tw-col-span-12">
                <Form.Group>
                  <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t('DashboardSettingCustomizeChat/l-12')}
                    {!permissions.settingsPage.widget.customization.theme && (
                      <ProBadge
                        className="tw-ms-2"
                        onClose={() => {
                          dispatch(handleResetAppearance());
                        }}
                      />
                    )}
                  </Form.Label>
                  <Form.Select
                    translate="no"
                    disabled={!permissions.settingsPage.widget.customization.theme}
                    isInvalid={!!errors.theme}
                    {...register('theme', {
                      onChange(event) {
                        dispatch(
                          handleUpdateAppearanceItem({
                            key: 'theme',
                            value: event.target.value,
                          })
                        );
                      },
                    })}
                  >
                    <option value={'light'}>{t('DashboardSettingCustomizeChat/l-05')}</option>
                    <option value={'dark'}>{t('DashboardSettingCustomizeChat/l-07')}</option>
                  </Form.Select>
                  {/* Validation */}
                  {errors.theme && <span className="tw-text-red-500 tw-text-sm tw-block">{errors.theme?.message}</span>}
                </Form.Group>
              </div>
              <div className="tw-w-full tw-col-span-12">
                <Form.Group>
                  <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t('DashboardSettingCustomizeChat/t-01')}
                    {!permissions.settingsPage.widget.customization.colors && (
                      <ProBadge
                        className="tw-ms-2"
                        onClose={() => {
                          dispatch(handleResetAppearance());
                        }}
                      />
                    )}
                  </Form.Label>
                  <Controller
                    control={control}
                    name="color"
                    render={({ field: { onChange, value } }) => (
                      <ColorDropDown
                        acceptableColors={[
                          { label: 'Grey', value: '#6B7280' },
                          { label: 'Red', value: '#EF4444' },
                          { label: 'Orange', value: '#F97316' },
                          { label: 'Yellow', value: '#F59E0B' },
                          { label: 'Green', value: '#10B981' },
                          { label: 'Teal', value: '#14B8A6' },
                          { label: 'Cyan', value: '#06B6D4' },
                          { label: 'Light Blue', value: '#0EA5E9' },
                          { label: 'Blue', value: '#3B82F6' },
                          { label: 'Indigo', value: '#6366F1' },
                          { label: 'Purple (Violet)', value: '#8B5CF6' },
                          { label: 'Pink', value: '#EC4899' },
                        ]}
                        disabled={!permissions.settingsPage.widget.customization.colors}
                        isInvalid={!!errors.color}
                        errorMessage={errors.color?.message}
                        onColorChanged={color => {
                          onChange(color);

                          dispatch(
                            handleUpdateAppearanceItem({
                              key: 'color',
                              value: color,
                            })
                          );
                        }}
                        currentColor={value}
                      />
                    )}
                  />
                </Form.Group>
              </div>
              <div className="tw-w-full tw-col-span-12">
                <Form.Group>
                  <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t('DashboardSettingCustomizeChat/t-02')}
                    {!permissions.settingsPage.widget.customization.position && <ProBadge className="tw-ms-2" />}
                  </Form.Label>
                  <Form.Select
                    translate="no"
                    disabled={!permissions.settingsPage.widget.customization.position}
                    isInvalid={!!errors.position}
                    {...register('position', {
                      onChange(event) {
                        dispatch(
                          handleUpdateAppearanceItem({
                            key: 'position',
                            value: event.target.value,
                          })
                        );
                      },
                    })}
                  >
                    <option value={'right'}>{t('DashboardSettingCustomizeChat/l-09')}</option>
                    <option value={'left'}>{t('DashboardSettingCustomizeChat/l-08')}</option>
                  </Form.Select>
                  {/* Validation */}
                  {errors.position && <span className="tw-text-red-500 tw-text-sm tw-block">{errors.position?.message}</span>}
                </Form.Group>
              </div>
            </div>
          </div>
        </div>

        <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

        {/* Floating button */}

        <IconCustomizationSection
          permissions={permissions}
          configs={configs}
          originalData={originalData}
          iconImageRef={iconImageRef}
          iconVideoRef={iconVideoRef}
          isIOS={isIOS}
          isAndroid={isAndroid}
          errors={errors}
          dispatch={dispatch}
          handleResetAppearance={handleResetAppearance}
          handleUpdateItem={handleUpdateItem}
        />

        <hr className="tw-border-gray-200 tw-pb-2 ty-m-0" />
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex">
          <Form.Group className="tw-w-full  tw-flex  tw-flex-col ">
            <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              {t('DashboardSettingCustomizeChat/l-19')}
              {!permissions.settingsPage.widget.customization.displayAfter && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
            </Form.Label>

            <Form.Control
              isInvalid={!!errors.displayAfter}
              maxLength={30}
              max={300}
              disabled={!permissions.settingsPage.widget.customization.displayAfter}
              {...register('displayAfter', {
                onChange: event => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: 'displayAfter',
                      value: event.target.value != '' ? event.target.value : null,
                    })
                  );
                },
              })}
              placeholder={'0'}
              type={'number'}
              className="tw-h-10 tw-w-32 tw-px-4 tw-py-2.5 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex"
            />
            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
              {errors?.displayAfter?.message as string}
            </Form.Control.Feedback>
          </Form.Group>
        </div>

        <AppearanceNotificationComponent register={register} watch={watch} className="tw-mt-6" />

        <AppearanceMessageNotificationComponent register={register} watch={watch} errors={errors} control={control} />

        <AppearanceMessageSound setValue={setValue} register={register} watch={watch} errors={errors} control={control} />

        <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

        {/* Voice Messages */}
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t('DashboardSettingCustomizeChat/l-16')}
          </div>
          <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
            <Form.Group className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
              <Form.Check
                disabled={!permissions.settingsPage.widget.customization.voiceCommand}
                type="switch"
                id="isVoiceActive"
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t('DashboardSettingCustomizeChat/allow-send-voice')}
                isInvalid={!!errors.isVoiceActive}
                feedbackType="invalid"
                feedback={errors.isVoiceActive?.message}
                {...register('isVoiceActive', {
                  onChange: e => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'isVoiceActive',
                        value: e.target.checked,
                      })
                    );
                  },
                })}
              />
              {!permissions.settingsPage.widget.customization.voiceCommand && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
              <PopoverWithAudio
                data={{
                  contentText: voiceCommandPopData.contentText,
                }}
              />
            </Form.Group>

            <AppearanceVoiceResponseToggle formContext={formContext} i18n={i18n} permissions={permissions} />
          </div>
        </div>
        <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

        {/* Other */}
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t('DashboardSettingCustomizeChat/other')}
          </div>
          <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
            <Form.Group className="tw-flex tw-items-center tw-gap-0 md:tw-gap-2">
              <Form.Check
                disabled={!permissions.settingsPage.widget.customization.save_conversation}
                type="switch"
                id="save_conversation"
                className="tw-flex tw-items-center tw-gap-1 md:tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t('DashboardSettingCustomizeChat/t-08')}
                isInvalid={!!errors.save_conversation}
                feedbackType="invalid"
                feedback={errors.save_conversation?.message}
                {...register('save_conversation', {
                  onChange: e => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'save_conversation',
                        value: e.target.checked,
                      })
                    );
                  },
                })}
              />
              {!permissions.settingsPage.widget.customization.save_conversation && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
              <PopoverWithAudio
                data={{
                  contentText: SaveConversationData.contentText,
                }}
              />
            </Form.Group>

            <AppearanceCustomPoweredBy
              hasPermission={permissions.settingsPage.widget.customization.poweredBy}
              isInvalid={!!errors.customPoweredBy}
              errors={errors.customPoweredBy}
              setFormValue={setValue}
              register={register}
              watch={watch}
            />

            <Form.Group className="tw-flex tw-items-center tw-gap-2">
              <Form.Check
                disabled={!permissions.settingsPage.widget.customization.defaultOpen}
                type="switch"
                id="isOpen"
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t('DashboardSettingCustomizeChat/l-04')}
                isInvalid={!!errors.isOpen}
                feedbackType="invalid"
                feedback={errors.isOpen?.message}
                {...register('isOpen', {
                  onChange: e => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'isOpen',
                        value: e.target.checked,
                      })
                    );
                  },
                })}
              />
              {!permissions.settingsPage.widget.customization.defaultOpen && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
            </Form.Group>

            {watch('isOpen') && (
              <Form.Group className="tw-flex tw-items-center tw-gap-2">
                <Form.Check
                  disabled={!permissions.settingsPage.widget.customization.isFullScreen}
                  type="switch"
                  id="isFullScreen"
                  className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                  label={t('DashboardSettingCustomizeChat/l-15')}
                  isInvalid={!!errors.isFullScreen}
                  feedbackType="invalid"
                  feedback={errors.isFullScreen?.message}
                  {...register('isFullScreen', {
                    onChange: e => {
                      dispatch(
                        handleUpdateAppearanceItem({
                          key: 'isFullScreen',
                          value: e.target.checked,
                        })
                      );
                    },
                  })}
                />
                {!permissions.settingsPage.widget.customization.isFullScreen && (
                  <ProBadge
                    className="tw-ms-2"
                    onClose={() => {
                      dispatch(handleResetAppearance());
                    }}
                  />
                )}
              </Form.Group>
            )}

            <Form.Group className="tw-flex tw-items-center tw-gap-2">
              <Form.Check
                type="switch"
                id="saveStats"
                disabled={!permissions.settingsPage.widget.customization.saveStats}
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t('DashboardSettingCustomizeChat/l-17')}
                isInvalid={!!errors.saveStats}
                feedbackType="invalid"
                feedback={errors.saveStats?.message}
                {...register('saveStats', {
                  onChange: e => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'saveStats',
                        value: e.target.checked,
                      })
                    );
                  },
                })}
              />
              {!permissions.settingsPage.widget.customization.saveStats && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}

              <PopoverWithAudio
                data={{
                  contentText: SaveStatsData.contentText,
                }}
              />
            </Form.Group>

            <AppearanceDownTime errors={errors} register={register} watch={watch} />

            <AppearanceCustomCss />
          </div>
        </div>
      </React.Fragment>
    </div>
  );
};

export default WrappedErrorBoundary(AppearanceSharedAi);

export const DropDownWithImage = ({
  onSelect,
  select,
  list = [],
  isAdvanced,
}: {
  onSelect: (item: any) => void;
  select?: string;
  list: {
    voice: string;
    gender: 'male' | 'female';
    isAdvanced: boolean;
  }[];
  isAdvanced: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const [item, setItem] = useState(list[0]);

  useEffect(() => {
    if (select) {
      const selectedItem = list?.find(item => item.voice === select);
      if (selectedItem) {
        setItem(selectedItem);
      }
    }
  }, [select]);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className="tw-relative  tw-w-full" ref={dropdownRef}>
        <div
          role="button"
          tabIndex={0}
          onClick={toggleDropdown}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') toggleDropdown();
          }}
          className="tw-w-full tw-h-9 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex"
        >
          <div className="tw-w[220px] tw-grow tw-shrink tw-basis-0 tw-h-9 tw-px-3 tw-py-2 tw-justify-start tw-items-center tw-gap-2 tw-flex">
            <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{item?.voice}</div>
          </div>
          <div className="tw-px-3 tw-py-2 tw-bg-white tw-rounded-md tw-shadow tw-justify-start tw-items-center tw-gap-1 tw-flex">
            {!isOpen && <BiChevronDown className="tw-w-5 tw-h-5 tw-relative" />}
            {isOpen && <BiChevronUp className="tw-w-5 tw-h-5 tw-relative" />}
          </div>
        </div>

        {isOpen && (
          <div id="dropdownUsers" className="tw-z-10 tw-absolute tw-top-[43px]  tw-bg-white tw-rounded-lg tw-shadow tw-w-60 tw-dark:bg-gray-700">
            <ul className="tw-list-none tw-h-48 tw-px-2 tw-py-2 tw-overflow-y-auto tw-text-gray-700 tw-dark:text-gray-200">
              {list?.map(item => (
                <li
                  key={item.voice + item.gender}
                  role="option"
                  aria-selected={false}
                  tabIndex={0}
                  onClick={() => {
                    if (isAdvanced || !item?.isAdvanced) {
                      onSelect(item);
                      toggleDropdown();
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      if (isAdvanced || !item?.isAdvanced) {
                        onSelect(item);
                        toggleDropdown();
                      }
                    }
                  }}
                >
                  <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal tw-flex tw-cursor-pointer tw-rounded-xl tw-px-1  tw-py-2 hover:tw-bg-gray-100 tw-items-center tw-hover:bg-gray-100 tw-dark:hover:bg-gray-600 tw-dark:hover:text-white tw-justify-between tw-align-center">
                    <div className="tw-flex tw-flex-row tw-gap-2 ">
                      {item.gender == 'male' && <AiOutlineUser className="tw-text-blue-600 tw-text-xl" />}
                      {item.gender == 'female' && <SlUserFemale className="tw-text-pink-600" />}
                      {item.voice}
                    </div>
                    {!isAdvanced && item?.isAdvanced && <BiSolidRocket className="tw-relative " />}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};
